import {
  SCORING_DEFAULTS,
  type AdvancedLeagueFormValues,
} from "./advancedLeagueSchema";
import {
  toCreateRoundBody,
  toDateOnly,
  toLocalDateOnly,
  type RoundMappingOptions,
} from "./roundMapping";
import type { CreateLeagueMutationArgs } from "../../api/endpoints/leagues";
import type {
  Clubhouse_PostLeagueV2,
  Clubhouse_ClubhouseLeagueSettingsDto,
  Clubhouse_LeagueV2ScoringPolicyInput,
  Clubhouse_LeagueV2SideContestInput,
  Clubhouse_LeagueV2PointRuleRowInput,
} from "../../types/leagues";
import { parse, getDay } from "date-fns";
import { resolveLeagueTimezone } from "../helpers";
import type { Clubhouse_LeagueOperatorDetailDto } from "../../types/leagueDetails";
import {
  Clubhouse_LeagueHandicapMethod,
  Clubhouse_LeagueScoring,
  Clubhouse_LeagueType,
  Clubhouse_LeagueWizardScheduleMode,
  Clubhouse_LgStrokePlayAggregation,
  Clubhouse_LgStrokePlayRanking,
  Clubhouse_LgStrokePlayTieHandling,
  Clubhouse_LgMatchPlayScoringMode,
  Clubhouse_LgPointsLeagueMode,
  Clubhouse_SideContestType,
  HandicapMode,
  HandicapPercentage,
  TeeType,
} from "../../enums";

type ScoringForm = NonNullable<AdvancedLeagueFormValues["scoringPolicy"]>;

// Custom-points table → API rows. Order and eventCode strings are the backend
// contract: double-bogey→eagle, sequence 1..5 (row index + 1). All five rows
// are always sent; scope defaults to 1.
const CUSTOM_POINT_ROWS: Array<
  [
    keyof NonNullable<NonNullable<ScoringForm["pointsLeague"]>["customPoints"]>,
    string,
  ]
> = [
  ["doubleBogey", "DOUBLE_BOGEY_OR_WORSE"],
  ["bogey", "BOGEY"],
  ["par", "PAR"],
  ["birdie", "BIRDIE"],
  ["eagle", "EAGLE"],
];

const SIDE_CONTESTS: Array<
  [keyof NonNullable<ScoringForm["sideContests"]>, Clubhouse_SideContestType]
> = [
  ["closestToPin", Clubhouse_SideContestType.ClosestToPin],
  ["longDrive", Clubhouse_SideContestType.LongDrive],
  ["skins", Clubhouse_SideContestType.Skins],
];

/** Build the API scoring-policy body from the active format's form block only. */
function buildScoringPolicy(
  values: AdvancedLeagueFormValues,
): Clubhouse_LeagueV2ScoringPolicyInput | undefined {
  const sp = values.scoringPolicy;
  if (!sp) return undefined;
  const fmt = values.settings.leagueScoringType;
  const policy: Clubhouse_LeagueV2ScoringPolicyInput = {
    // Handicap is always applied server-side; it's no longer an operator field.
    handicapEnabled: true,
  };

  if (fmt === Clubhouse_LeagueScoring.STROKE) {
    policy.strokePlay = {
      // Cumulative + relative-to-par are fixed (not surfaced in the UI).
      aggregation: Clubhouse_LgStrokePlayAggregation.CUMULATIVE,
      relativeToPar: true,
      // Leaderboard rules are defaulted behind the scenes (no operator UI).
      ranking:
        sp.strokePlay?.ranking ?? Clubhouse_LgStrokePlayRanking.TOTAL_TO_PAR,
      tieHandling:
        sp.strokePlay?.tieHandling ?? Clubhouse_LgStrokePlayTieHandling.SHARED,
    };
  } else if (fmt === Clubhouse_LeagueScoring.MATCH) {
    const m = sp.matchPlay ?? {};
    const holeBased =
      m.matchScoringMode === Clubhouse_LgMatchPlayScoringMode.HOLE_BY_HOLE;
    policy.matchPlay = {
      matchScoringMode: m.matchScoringMode,
      holeBasedPointsEnabled: holeBased,
      ...(holeBased
        ? {
            holeWonPoints: m.holeWonPoints,
            holeTiedPoints: m.holeTiedPoints,
            holeLostPoints: m.holeLostPoints,
          }
        : {
            winPoints: m.winPoints,
            tiePoints: m.tiePoints,
            lossPoints: m.lossPoints,
          }),
    };
  } else if (fmt === Clubhouse_LeagueScoring.POINTS) {
    const p = sp.pointsLeague ?? {};
    policy.pointsLeague = {
      mode: p.mode ?? Clubhouse_LgPointsLeagueMode.STABLEFORD,
      maxPointsPerHole: p.maxPointsPerHole,
    };
    if (
      p.mode === Clubhouse_LgPointsLeagueMode.CUSTOM_TABLE &&
      p.customPoints
    ) {
      const cp = p.customPoints;
      policy.customPointRules = CUSTOM_POINT_ROWS.map(
        ([key, eventCode], i): Clubhouse_LeagueV2PointRuleRowInput => ({
          eventCode,
          pointsValue: cp[key],
          sequence: i + 1,
          scope: 1,
        }),
      );
    }
  }
  return policy;
}

/** Side contests apply to Match and Points formats only. */
function buildSideContests(
  values: AdvancedLeagueFormValues,
): Clubhouse_LeagueV2SideContestInput[] | undefined {
  const sc = values.scoringPolicy?.sideContests;
  const fmt = values.settings.leagueScoringType;
  if (
    !sc ||
    (fmt !== Clubhouse_LeagueScoring.MATCH &&
      fmt !== Clubhouse_LeagueScoring.POINTS)
  ) {
    return undefined;
  }
  return SIDE_CONTESTS.map(([key, contestType]) => ({
    contestType,
    isEnabled: sc[key].enabled,
    pointsValue: sc[key].points,
  }));
}

type StoredCustomPoints = NonNullable<
  NonNullable<ScoringForm["pointsLeague"]>["customPoints"]
>;

const STORED_POINT_KEYS: Record<string, keyof StoredCustomPoints> = {
  DOUBLE_BOGEY_OR_WORSE: "doubleBogey",
  BOGEY: "bogey",
  PAR: "par",
  BIRDIE: "birdie",
  EAGLE: "eagle",
};

/** Saved lg_point_rules, with the create-form table only filling codes the league did not store. */
function customPointsFromStoredRules(
  rules: Clubhouse_LeagueV2PointRuleRowInput[] | undefined,
): StoredCustomPoints {
  // SCORING_DEFAULTS is const, so a bare spread keeps literal point values and
  // a later write is `never`. Widen to the form's number fields.
  const customPoints: StoredCustomPoints = {
    ...SCORING_DEFAULTS.pointsLeague.customPoints,
  };
  for (const row of rules ?? []) {
    const key = STORED_POINT_KEYS[row.eventCode];
    if (key != null && row.pointsValue != null) {
      customPoints[key] = row.pointsValue;
    }
  }
  return customPoints;
}

/** Hydrate the form scoring policy from a league detail (edit mode). */
function scoringPolicyFromDetail(
  d: Clubhouse_LeagueOperatorDetailDto["leagueV2Detail"],
): ScoringForm {
  const policy = d.scoringPolicy?.policy;
  const contests = d.sideContests ?? [];
  const find = (t: Clubhouse_SideContestType) =>
    contests.find((c) => c.contestType === t);
  const contest = (
    key: keyof NonNullable<ScoringForm["sideContests"]>,
    t: Clubhouse_SideContestType,
  ) => {
    const hit = find(t);
    const dflt = SCORING_DEFAULTS.sideContests[key];
    return {
      enabled: hit?.isEnabled ?? dflt.enabled,
      points: hit?.pointsValue ?? dflt.points,
    };
  };

  return {
    strokePlay: policy?.strokePlay
      ? {
          ranking: policy.strokePlay.ranking,
          tieHandling: policy.strokePlay.tieHandling,
        }
      : SCORING_DEFAULTS.strokePlay,
    matchPlay: policy?.matchPlay
      ? {
          matchScoringMode: policy.matchPlay.matchScoringMode,
          winPoints: policy.matchPlay.winPoints,
          tiePoints: policy.matchPlay.tiePoints,
          lossPoints: policy.matchPlay.lossPoints,
          holeWonPoints: policy.matchPlay.holeWonPoints,
          holeTiedPoints: policy.matchPlay.holeTiedPoints,
          holeLostPoints: policy.matchPlay.holeLostPoints,
        }
      : SCORING_DEFAULTS.matchPlay,
    pointsLeague: policy?.pointsLeague
      ? {
          mode: policy.pointsLeague.mode,
          maxPointsPerHole: policy.pointsLeague.maxPointsPerHole,
          customPoints: customPointsFromStoredRules(
            d.scoringPolicy?.customPointRules,
          ),
        }
      : SCORING_DEFAULTS.pointsLeague,
    sideContests: {
      closestToPin: contest(
        "closestToPin",
        Clubhouse_SideContestType.ClosestToPin,
      ),
      longDrive: contest("longDrive", Clubhouse_SideContestType.LongDrive),
      skins: contest("skins", Clubhouse_SideContestType.Skins),
    },
  };
}

/**
 * Fallback values for the settings fields the API requires but that may be
 * absent when a draft is saved before the operator visits the Roster/Scoring
 * tabs. The create/PATCH endpoints validate the full settings DTO whenever
 * `settings` is present, so these fill the gaps; any value the operator does
 * enter overrides them via buildSettings. Values mirror the API's documented
 * sample (a known valid combination). NOTE: participantCap 0 means "no cap"
 * (the schema's min is 1, so 0 is never a user-entered value) — confirmed
 * backend behaviour, and what Guided Setup sends too.
 */
const DEFAULT_DRAFT_SETTINGS = {
  participantCap: 0,
  handicapMethod: Clubhouse_LeagueHandicapMethod.NET,
  handicapMode: HandicapMode.ABSOLUTE,
  handicapPercentage: HandicapPercentage.SEVENTY,
  courseRatingSlope: true,
  leagueScoringType: Clubhouse_LeagueScoring.STROKE,
} as const;

/** Merge the operator's chosen settings over the required-field defaults. */
function buildSettings(
  settings: AdvancedLeagueFormValues["settings"],
): Clubhouse_ClubhouseLeagueSettingsDto {
  const { leagueTeamSize, ...rest } = settings;
  return {
    ...rest,
    // Individual leagues reject a leftover team size from a prior TEAM/Scramble
    // selection. Omit it so PATCH/create matches the API create rules.
    ...(settings.leagueType === Clubhouse_LeagueType.INDIVIDUAL
      ? {}
      : { leagueTeamSize }),
    participantCap:
      settings.participantCap ?? DEFAULT_DRAFT_SETTINGS.participantCap,
    handicapMethod:
      settings.handicapMethod ?? DEFAULT_DRAFT_SETTINGS.handicapMethod,
    // Always the defaults: handicapping is backend-owned and these have no UI,
    // but they are sent rather than omitted because they were optional on the
    // way in and the backend most likely does not default them. Sending a
    // known-valid combination beats leaving the league with none.
    handicapMode: DEFAULT_DRAFT_SETTINGS.handicapMode,
    handicapPercentage: DEFAULT_DRAFT_SETTINGS.handicapPercentage,
    courseRatingSlope: DEFAULT_DRAFT_SETTINGS.courseRatingSlope,
    leagueScoringType:
      settings.leagueScoringType ?? DEFAULT_DRAFT_SETTINGS.leagueScoringType,
    // mensTee/womensTee are NOT defaulted here (unlike the fields above): the
    // operator must explicitly choose a tee in create mode, and the schema +
    // Save Draft's validation gate are what guarantee a real value reaches
    // this point — silently filling White/Red here would defeat that.
  } as Clubhouse_ClubhouseLeagueSettingsDto;
}

/**
 * Options for embedding the form's round plan in a league create or replace.
 *
 * Season Flex is stored when every round omits both play windows. Filling the
 * league dates in as a window leaves schedule mode unset, and the API then
 * persists Weekly — which is what the dashboard and league header display.
 * The single-round POST is a different contract and still uses that fallback.
 */
function seasonRoundWireOptions(
  values: AdvancedLeagueFormValues,
  tz?: string,
): RoundMappingOptions {
  if (values.scheduleMode === Clubhouse_LeagueWizardScheduleMode.SeasonFlex) {
    return { timezone: tz, omitPlayWindow: true };
  }
  return {
    timezone: tz,
    seasonStartUtc: toDateOnly(values.leagueStartDate, "start", {
      timezone: tz,
    }),
    seasonEndUtc: toDateOnly(values.leagueEndDate, "end", { timezone: tz }),
  };
}

/** Build the createLeague POST body from advanced form values.
 *  Required settings the UI hasn't collected yet are filled from defaults.
 *  `participatingClubhouseFacilityIds` is derived by the caller (see the
 *  `helpers` export of the same name) and sent for multi-facility leagues. */
export function toCreateLeagueArgs(
  values: AdvancedLeagueFormValues,
  tz?: string,
  participatingClubhouseFacilityIds?: number[],
): CreateLeagueMutationArgs {
  const seasonTimezone = resolveLeagueTimezone(
    values.leagueStartDateTimezone,
    tz,
  );

  return {
    facilityId: values.facilityId,
    name: values.name,
    description: values.description,
    leagueStartDate: values.leagueStartDate,
    leagueEndDate: values.leagueEndDate,
    leagueStartDateTimezone: seasonTimezone,
    leagueEndDateTimezone: resolveLeagueTimezone(
      values.leagueEndDateTimezone,
      seasonTimezone,
    ),
    facilityScope: values.facilityScope,
    participatingClubhouseFacilityIds,
    // Substitutes are a per-player role (Roster tab), not an operator toggle —
    // every league allows them, so always enable at the league level.
    allowSubstitutes: true,
    scoringPolicy: buildScoringPolicy(values),
    sideContests: buildSideContests(values),
    settings: buildSettings(values.settings),
    // The API stores this. Omitting it and sending a play window is what made
    // a Flex create persist as Weekly.
    ...(values.scheduleMode ? { wizardScheduleMode: values.scheduleMode } : {}),
    // Season Setup generates the rounds client-side and holds them in
    // `seasonRounds`; send them so the backend persists exactly what the
    // operator sees instead of auto-generating. The API requires
    // seasonRoundCount to equal seasonRounds.length when both are present, so
    // derive the count from the rounds rather than the form's own field.
    ...(values.seasonRounds?.length
      ? {
          seasonRoundCount: values.seasonRounds.length,
          seasonRounds: values.seasonRounds.map((round) =>
            toCreateRoundBody(
              round,
              seasonRoundWireOptions(values, seasonTimezone),
            ),
          ),
        }
      : {}),
  };
}

/** Map an existing league's detail (getLeague) into editor form values for edit mode. */
export function fromLeagueDetail(
  detail: Clubhouse_LeagueOperatorDetailDto,
  timezone?: string,
): Partial<AdvancedLeagueFormValues> {
  const d = detail.leagueV2Detail;
  timezone =
    timezone ??
    d.season?.schedulingTimezone ??
    detail.metaData?.schedulingTimezone;

  const recentRounds = detail.rounds?.recentRounds ?? [];
  const windowedRound = recentRounds.find((r) => !!r.playWindowStartUtc);
  const totalRounds = detail.rounds?.totalRounds ?? recentRounds.length;

  // The league exists by the time this runs, so its own season.scheduleMode is
  // authoritative. The round-shape inference below is the fallback it was
  // written as — from when the detail didn't carry the field — and is now only
  // reached for a league saved before it did.
  //
  // Inferring is also not equivalent: it reads "no round has a play window" as
  // Flex, so a Flex league whose rounds happen to carry windows derives as
  // Weekly, and the editor would then disagree with every other surface that
  // reads scheduleMode directly.
  const inferredScheduleMode =
    totalRounds === 0
      ? undefined
      : windowedRound
        ? Clubhouse_LeagueWizardScheduleMode.WeeklyRounds
        : Clubhouse_LeagueWizardScheduleMode.SeasonFlex;
  const scheduleMode = d.season?.scheduleMode ?? inferredScheduleMode;
  const windowedRoundLocalDate = toLocalDateOnly(
    windowedRound?.playWindowStartUtc,
    timezone,
    "start",
  );
  const weekDay = windowedRoundLocalDate
    ? getDay(parse(windowedRoundLocalDate, "yyyy-MM-dd", new Date()))
    : undefined;

  return {
    name: d.name ?? "",
    description: d.description ?? undefined,
    facilityScope: d.facilityScope,
    leagueStartDate:
      toLocalDateOnly(d.season?.startDateUtc, timezone, "start") ?? "",
    leagueEndDate: toLocalDateOnly(d.season?.endDateUtc, timezone, "end") ?? "",
    leagueStartDateTimezone: timezone,
    leagueEndDateTimezone: timezone,
    scheduleMode,
    weekDay,
    // Flex has no windows to regenerate from, so seed the count from the total.
    seasonRoundCount:
      scheduleMode === Clubhouse_LeagueWizardScheduleMode.SeasonFlex
        ? totalRounds
        : undefined,
    // settings.leagueType is required in the schema; a real league always has it.
    settings: {
      leagueType: d.leagueType,
      leagueScoringType: d.scoring,
      participantCap: d.participantCap,
      leagueTeamSize: d.teamSize,
      defaultGroupSize: d.groupSize,
      allowAsynchronousMatchPlay: d.allowAsyncMatch,
      allowAsynchronousTeamPlay: d.allowAsyncTeam,
      mensTee: d.mensTee ?? TeeType.WHITE,
      womensTee: d.womensTee ?? TeeType.RED,
    } as AdvancedLeagueFormValues["settings"],
    scoringPolicy: scoringPolicyFromDetail(d),
  };
}

/** Top-level dirty map from react-hook-form's formState.dirtyFields. A key is
 *  truthy (a `true` or a nested object) when that field was edited. */
type DirtyFieldMap = Partial<Record<keyof AdvancedLeagueFormValues, unknown>>;

/**
 * Build the partial PATCH body for editing an existing league.
 *
 * When `dirty` is provided, only the fields the operator actually changed are
 * sent. This is essential in edit mode: `fromLeagueDetail` cannot hydrate every
 * field the league has (the detail DTO doesn't expose handicapEnabled or
 * allowSubstitutes), so sending an untouched field would overwrite the saved
 * value with a form default. Omit `dirty` to send the full editable body.
 *
 * The API enforces "a date and its timezone must both be present or both
 * omitted", so each date is sent together with its timezone. When any `settings`
 * sub-field is dirty the full settings DTO is sent (the endpoint validates the
 * whole object whenever `settings` is present).
 */
export function toUpdateLeagueBody(
  values: AdvancedLeagueFormValues,
  tz?: string,
  dirty?: DirtyFieldMap,
  participatingClubhouseFacilityIds?: number[],
): Partial<Clubhouse_PostLeagueV2> {
  const include = (key: keyof AdvancedLeagueFormValues) =>
    !dirty || !!dirty[key];
  const body: Partial<Clubhouse_PostLeagueV2> = {};

  if (include("name")) body.name = values.name;
  if (include("description")) body.description = values.description;
  if (include("leagueStartDate")) {
    body.leagueStartDate = values.leagueStartDate;
    body.leagueStartDateTimezone = resolveLeagueTimezone(
      values.leagueStartDateTimezone,
      tz,
    );
  }
  if (include("leagueEndDate")) {
    body.leagueEndDate = values.leagueEndDate;
    body.leagueEndDateTimezone = resolveLeagueTimezone(
      values.leagueEndDateTimezone,
      tz,
    );
  }
  // Unchecking a facility while staying multi-facility changes the participating
  // list without changing the scope, so send the pair when EITHER is dirty.
  if (
    include("facilityScope") ||
    include("participatingClubhouseFacilityIds")
  ) {
    body.facilityScope = values.facilityScope;
    body.participatingClubhouseFacilityIds = participatingClubhouseFacilityIds;
  }
  // Create-mode rounds remain in the form after the initial draft POST. A
  // later per-round course edit dirties `seasonRounds`, so include the full
  // replacement list in the PATCH used by Save draft and Finalize.
  if (include("seasonRounds") && values.seasonRounds?.length) {
    body.seasonRounds = values.seasonRounds.map((round) =>
      toCreateRoundBody(round, seasonRoundWireOptions(values, tz)),
    );
  }
  if (include("allowSubstitutes"))
    body.allowSubstitutes = values.allowSubstitutes;
  // The scoring format lives in settings.leagueScoringType, but it decides which
  // scoringPolicy block to send — so a format-only change must still emit the
  // policy even though `scoringPolicy` itself isn't dirty.
  const formatDirty = !!(
    dirty?.settings as { leagueScoringType?: unknown } | undefined
  )?.leagueScoringType;
  if (include("scoringPolicy") || formatDirty) {
    body.scoringPolicy = buildScoringPolicy(values);
    body.sideContests = buildSideContests(values);
  }
  if (include("settings")) body.settings = buildSettings(values.settings);

  return body;
}
